import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import { InjectionToken } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { environment } from '@env';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, RouterStateSerializer } from '@ngrx/router-store';
import type { ActionReducer, ActionReducerMap, MetaReducer } from '@ngrx/store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { PROTECTED_URIS } from '@tokens/protected-uris.token';

import { ENVIRONMENT, getAuthProviders, provideEnv } from '@evc/platform';
import { getI18nProviders } from '@shared/services/i18n/i18n.provider';

import { ndtFileManagerStoreEffects } from '@app/store/effects';
import type { NdtFileManagerState } from '@app/store/reducers';
import { ndtFileManagerStoreReducers } from '@app/store/reducers';
import { CustomRouterStateSerializer } from '@app/utils/routing/custom-state.serializer';
import { routes } from './app.routes';

export const FEATURE_REDUCER_TOKEN: InjectionToken<ActionReducerMap<NdtFileManagerState>> =
  new InjectionToken<ActionReducerMap<NdtFileManagerState>>('File Manager Reducers');

export function getReducers(): ActionReducerMap<NdtFileManagerState> {
  return ndtFileManagerStoreReducers;
}

const isProdMode =
  environment.ENV === ENVIRONMENT.production || environment.ENV === ENVIRONMENT.staging;

const metaReducers: MetaReducer<any>[] = !isProdMode ? [stateSetter] : [];

export const appConfig: ApplicationConfig = {
  providers: [
    provideEnv(environment),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),

    // Auth, i18n
    { provide: PROTECTED_URIS, useValue: environment.PROTECTED_URIS },
    ...getAuthProviders(environment),
    ...getI18nProviders(),

    // NgRx Store
    { provide: FEATURE_REDUCER_TOKEN, useFactory: getReducers },
    { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
    provideStore(
      {},
      {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
      },
    ),
    provideEffects(ndtFileManagerStoreEffects),
    provideState('ndtFileManager', FEATURE_REDUCER_TOKEN),
    provideRouterStore({
      serializer: CustomRouterStateSerializer,
      stateKey: 'router',
    }),
    ...(isProdMode ? [] : [provideStoreDevtools()]),
  ],
};

function mutateObjectProperty(prop: any, newValue: any, object: any, ignoreValues: any[] = []) {
  return JSON.parse(JSON.stringify(object), (key: string, value: any) =>
    key === prop && !ignoreValues.includes(value) ? newValue : value,
  );
}

export function stateSetter(reducer: ActionReducer<any>): ActionReducer<any> {
  return (state: any, action: any) => {
    if (action.type === 'SET_ROOT_STATE') {
      return mutateObjectProperty('lastLoaded', Date.now(), { ...action.payload }, [0, null]);
    }

    return reducer(state, action);
  };
}
